import React, { useState, useEffect } from "react";
import { Card, Form, Space, Typography, Button, App, Spin } from "antd";
import type { UploadFile } from "antd/es/upload";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import RobinPage from "../components/RobinPage";
import { FinanceTransactionForm } from "../components/FinanceTransactionForm";
import { useUploadFileMutation, useDeleteMediaMutation } from "../graphql/generated/hooks";
import { useFinanceTreeItems } from "../hooks/useFinanceTreeItems";
import { useFinanceTransaction } from "../hooks/useFinanceTransaction";
import { useUpdateFinanceTransaction } from "../hooks/useUpdateFinanceTransaction";
import { ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { TransactionLine } from "../types/financeTransaction.types";

const { Text } = Typography;

function FinanceTransactionEdit() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const { message: messageApi } = App.useApp();

  // State
  const [transactionLines, setTransactionLines] = useState<TransactionLine[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadedMediaData, setUploadedMediaData] = useState<any>(null); // Single object
  const [saving, setSaving] = useState(false);

  // Fetch finance tree items
  const { data: financeTreeData, loading: financeTreeLoading } = useFinanceTreeItems();
  const [uploadFile, { loading: uploading }] = useUploadFileMutation();
  const [deleteMedia, { loading: deletingMedia }] = useDeleteMediaMutation();
  const [updateFinanceTransaction, { loading: updating }] = useUpdateFinanceTransaction();
  
  // Fetch transaction data
  const { data: transactionData, loading } = useFinanceTransaction(parseInt(id || "0"));

  // Load transaction data into form
  useEffect(() => {
    if (transactionData?.getFinanceTreeTransaction) {
      const transaction = transactionData.getFinanceTreeTransaction;
      
      // Set form values
      form.setFieldsValue({
        date: transaction.date ? dayjs(transaction.date) : null,
        description: transaction.description || "",
      });

      // Set transaction lines
      const lines: TransactionLine[] = transaction.transactionLines.map((line, index) => ({
        key: line.id.toString(),
        financeItemId: line.financeTreeId,
        description: line.description || "",
        debit: line.debit,
        credit: line.credit,
      }));
      setTransactionLines(lines);

      // Set file list and media data if image exists
      if (transaction.image) {
        const uploadFile: UploadFile = {
          uid: transaction.image.id.toString(),
          name: transaction.image.originalName,
          status: "done",
          url: transaction.image.url,
          type: transaction.image.mimeType,
          size: transaction.image.size,
          response: {
            id: transaction.image.id,
            url: transaction.image.url,
          },
        };
        setFileList([uploadFile]);
        
        // Set uploaded media data for update (single object)
        setUploadedMediaData({
          id: transaction.image.id,
          fileName: transaction.image.fileName,
          originalName: transaction.image.originalName,
          mimeType: transaction.image.mimeType,
          size: transaction.image.size,
          type: transaction.image.type,
          url: transaction.image.url,
          altText: transaction.image.altText,
          description: transaction.image.description,
        });
      }
    }
  }, [transactionData, form]);

  // Get finance tree items from response
  const financeTreeItems = financeTreeData?.getAllFinanceTreeItems || [];

  // Calculate level based on serialNumber (count dots)
  const calculateLevel = (serialNumber: string): number => {
    return (serialNumber.match(/\./g) || []).length;
  };

  // Create indented options for the select dropdown
  const financeTreeOptions = financeTreeItems.map((item) => {
    const level = calculateLevel(item.serialNumber);
    return {
      label: `${"  ".repeat(level)}${item.serialNumber} - ${currentLang === "ar" ? item.nameAr : item.name}`,
      value: item.id,
      level: level,
    };
  });

  // Convert file to base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  // Real upload function using GraphQL (from PartnerCategoryList pattern)
  const handleCustomUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    
    try {
      onProgress({ percent: 0 });
      
      // Convert file to base64
      const base64String = await convertFileToBase64(file);
      onProgress({ percent: 50 });
      
      // Upload using GraphQL mutation
      const result = await uploadFile({
        variables: {
          base64File: base64String,
          originalName: file.name,
          folder: 'finance-transactions',
        },
      });
      
      onProgress({ percent: 100 });
      
      if (result.data?.uploadFile) {
        const mediaData = {
          id: result.data.uploadFile.id,
          fileName: result.data.uploadFile.fileName,
          originalName: result.data.uploadFile.originalName,
          mimeType: result.data.uploadFile.mimeType,
          size: result.data.uploadFile.size,
          type: 'DOCUMENT',
          url: result.data.uploadFile.url,
          altText: result.data.uploadFile.altText,
          description: result.data.uploadFile.description,
        };
        
        // Replace with new media (last upload wins)
        setUploadedMediaData(mediaData);
        
        onSuccess({
          id: result.data.uploadFile.id,
          url: result.data.uploadFile.url,
          mediaData: mediaData, // Store full media data for future use
        }, file);
      } else {
        onError(new Error('Upload failed: No data returned'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      onError(error);
    }
  };

  const handleRemove = async (file: any) => {
    try {
      // If the file has been uploaded and has an ID, delete it from the server
      if (file.response?.id) {
        await deleteMedia({
          variables: {
            id: file.response.id,
          },
        });
        
        // Clear uploaded media data
        setUploadedMediaData(null);
        
        messageApi.success(`${file.name} deleted successfully`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      messageApi.error(`Failed to delete ${file.name}`);
    }
  };

  // Handle form submission
  const handleSubmit = async (values: any) => {
    // Validate that we have at least one line with data
    const validLines = transactionLines.filter(
      (line) => line.financeItemId && (line.debit || line.credit)
    );

    if (validLines.length === 0) {
      messageApi.error(t("error.transaction.no_valid_lines"));
      return;
    }

    try {
      setSaving(true);

      // Format transaction lines for the mutation
      const transactionLinesInput = validLines.map((line) => ({
        financeTreeId: line.financeItemId!,
        description: line.description || "",
        debit: line.debit || undefined,
        credit: line.credit || undefined,
      }));

      // Format media data if exists (single object)
      const mediaInput = uploadedMediaData ? {
        fileName: uploadedMediaData.fileName,
        originalName: uploadedMediaData.originalName,
        mimeType: uploadedMediaData.mimeType,
        size: uploadedMediaData.size,
        type: uploadedMediaData.type,
        url: uploadedMediaData.url,
        isActive: true,
        altText: uploadedMediaData.altText || values.description || "Transaction attachment",
        description: uploadedMediaData.description || values.description || "",
      } : null;

      // Call GraphQL mutation to update the transaction
      const result = await updateFinanceTransaction({
        variables: {
          id: parseInt(id || "0"),
          updateFinanceTreeTransactionDto: {
            date: values.date?.format("YYYY-MM-DD"),
            description: values.description || "",
            media: mediaInput,
            transactionLines: transactionLinesInput,
          },
        },
      });

      if (result.data?.updateFinanceTreeTransaction) {
        messageApi.success(
          `${t("message.success.data_updated")} - ${result.data.updateFinanceTreeTransaction.transactionNumber}`
        );

        // Navigate back to list
        setTimeout(() => {
          navigate("/finance/transactions");
        }, 1500);
      }
    } catch (error: any) {
      console.error("Update transaction error:", error);
      messageApi.error(error.message || t("message.error.data_update_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/finance/transactions");
  };

  if (loading) {
    return (
      <RobinPage title={t("label.finance_transaction.edit_title")}>
        <Card
          bordered={false}
          style={{
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            textAlign: "center",
            padding: "80px 0",
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">{t("label.loading")}</Text>
          </div>
        </Card>
      </RobinPage>
    );
  }

  return (
    <RobinPage
      title={t("label.finance_transaction.edit_title")}
      actions={
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/finance/transactions")}>
          {t("button.back")}
        </Button>
      }
    >
      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        title={
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 16 }}>
              {t("label.finance_transaction.edit_title")}
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t("label.finance_transaction_description")}
            </Text>
          </Space>
        }
      >
        <FinanceTransactionForm
          form={form}
          transactionLines={transactionLines}
          setTransactionLines={setTransactionLines}
          financeTreeOptions={financeTreeOptions}
          financeTreeLoading={financeTreeLoading}
          fileList={fileList}
          setFileList={setFileList}
          handleCustomUpload={handleCustomUpload}
          handleRemove={handleRemove}
          uploading={uploading}
          isViewMode={false}
          isEditMode={true}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          saving={saving || updating}
          messageApi={messageApi}
        />
      </Card>
    </RobinPage>
  );
}

export default FinanceTransactionEdit;

